import { SpriteLabJob, SpriteLabState } from "game-common/spritelab"

// What the SpriteLab panel says, kept apart from how it is drawn: every string here is something a
// player reads to decide what to do next, and all of it is testable without a canvas.

// Older servers send counts only; these were their fixed limits.
export const DEFAULT_GENERATION_SLOTS = 2
export const DEFAULT_QUEUE_LIMIT = 8

export const generationSlots = (state: SpriteLabState): number => state.generationSlots ?? DEFAULT_GENERATION_SLOTS
export const queueLimit = (state: SpriteLabState): number => state.queueLimit ?? DEFAULT_QUEUE_LIMIT

/** m:ss, or h:mm:ss past an hour (a stuck job should still read as a time, not as nonsense). */
export const formatElapsed = (ms: number): string => {
    const total = Math.max(0, Math.floor((Number.isFinite(ms) ? ms : 0) / 1000))
    const seconds = total % 60
    const minutes = Math.floor(total / 60) % 60
    const hours = Math.floor(total / 3600)
    const ss = String(seconds).padStart(2, "0")
    return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${ss}` : `${minutes}:${ss}`
}

const KIND_LABEL = { creature: "creature", equipment: "equipment" } as const

const clip = (value: string, max: number): string => {
    const flat = value.replace(/\s+/g, " ").trim()
    return flat.length > max ? flat.slice(0, max - 1).trimEnd() + "…" : flat
}

/**
 * The row's name. The content id once the agent has picked one (that is what Spawn will list), the
 * description until then, and for another lab's job -- whose description is private -- only its kind.
 */
export const jobTitle = (job: SpriteLabJob, max = 40): string => {
    if (job.draftId) return clip(job.draftId, max)
    if (job.prompt) return clip(job.prompt, max)
    return job.mine ? `Your ${KIND_LABEL[job.kind]}` : `Another lab's ${KIND_LABEL[job.kind]}`
}

/** Who asked, when it is worth saying: never for your own job, and only when the server shared it. */
export const jobOwner = (job: SpriteLabJob): string => job.mine ? "you" : job.player || ""

/**
 * The right-hand column: what is happening and for how long. `sinceMs` is local time since the
 * state arrived, so the clock keeps running between the server's two-second polls.
 */
export const jobStatus = (job: SpriteLabJob, sinceMs = 0): string => {
    const clock = formatElapsed(job.phaseMs + sinceMs)
    if (job.phase === "generating") return `Drawing ${clock}`
    if (job.phase === "queued") return job.queuePosition ? `Queued #${job.queuePosition}` : "Queued"
    if (job.phase === "publishing") return `Publishing ${clock}`
    if (job.phase === "done") return "Live"
    return "Failed"
}

export const isJobActive = (job: SpriteLabJob): boolean =>
    job.phase === "generating" || job.phase === "queued" || job.phase === "publishing"

/**
 * Why Generate cannot be pressed right now, or null when it can. The panel shows this line in place
 * of the hint, so a greyed-out button is never left unexplained.
 */
export const generateBlocker = (state: SpriteLabState | null, prompt: string, pending = false): string | null => {
    if (!state) return "Not in a SpriteLab room"
    if (!state.available) return "Publishing is unavailable on this server"
    if (pending) return "Sending…"
    if (state.ownPhase) return "Your make is still in progress"
    if (state.generating >= generationSlots(state)) return "Both generation slots are busy"
    if (state.generating + state.queued + state.publishing >= queueLimit(state)) return "The publication queue is full"
    if (!prompt.trim()) return "Describe what to make"
    return null
}

/** The minimized header's one line: your own job if you have one, else the server's activity. */
export const activitySummary = (state: SpriteLabState, sinceMs = 0): string => {
    const own = state.jobs?.find(job => job.mine && isJobActive(job))
    if (own) return jobStatus(own, sinceMs)
    // Short enough for the folded header: drawing is what holds the slots; the rest is just waiting its turn.
    const waiting = state.queued + state.publishing
    if (state.generating) return waiting ? `${state.generating} drawing · ${waiting} more` : `${state.generating} drawing`
    if (state.publishing) return state.queued ? `Publishing · ${state.queued} queued` : "Publishing"
    if (state.queued) return `${state.queued} queued`
    const recent = state.jobs?.find(job => job.mine && !isJobActive(job))
    if (recent) return recent.phase === "done" ? "Your make is live" : "Your make failed"
    return "Idle"
}

export const isBusy = (state: SpriteLabState | null): boolean =>
    !!state && state.generating + state.queued + state.publishing > 0

// What the Spawn list calls a creation. The lab- prefix is on every entry, so it says nothing.
export const creationLabel = (id: string): string => id.replace(/^lab-/, "")

export const pageCount = (total: number, perPage: number): number => Math.max(1, Math.ceil(total / Math.max(1, perPage)))
export const clampPage = (page: number, total: number, perPage: number): number =>
    Math.max(0, Math.min(page, pageCount(total, perPage) - 1))
